import { Button, Card, Col, Row, Segmented, Spin } from "antd";
import Coin from "@assets/img/Coin.png";
import "./Campaign.scss";
import { useEffect, useState } from "react";
import GanttChart from "@components/GanttChart/GanttChart";
import businessLogicApi from "services/BusinessLogicApi";
import Helpers from "@utils/Helpers";
import CampaignList from "../CampaignList/CampaignList";
import { ICampaign } from "@utils/Interfaces";
import { DefaultOptionType } from "antd/es/select";
import { useTour } from "@reactour/tour";
import CampaignForm from "@components/CampaignForm/CampaignForm";
import BeginModal from "@components/BeginModal/BeginModal";
import ConfirmModal from "@components/ConfirmModal/ConfirmModal";
import { CreateCampaignSteps } from "@components/GuidedTour/steps/CreateCampaignSteps";

const Campaign: React.FC = () => {
  const { isOpen, setIsOpen, setCurrentStep, setSteps } = useTour();
  const [isFetch, setIsFetch] = useState(false);
  const [isOpenCreateForm, setIsOpenCreateForm] = useState(false);
  const [isOpenConfirmForm, setIsOpenConfirmForm] = useState(false);
  const [isBeginModal, setIsBeginModal] = useState(false);
  const [isCreateNew, setIsCreateNew] = useState(false);
  const [campaignList, setCampaignList] = useState<ICampaign[]>([]);
  const [positionList, setPositionList] = useState([]);
  const [levelList, setLevelList] = useState<string[]>([]);
  const [ganttView, setGanttView] = useState("Daily");
  const [tasks, setTasks] = useState<any[]>([]);
  const [isDateAllPhaseChange, setIsDateAllPhaseChange] = useState(false);
  const [startDateAllPhase, setStartDateAllPhase] = useState<Date | null>();
  const [endDateAllPhase, setEndDateAllPhase] = useState<Date | null>();
  const [campaignForm, setCampaignForm] = useState({
    campaign_name: "",
    budget: 0,
    start_date: "",
    end_date: "",
    duration: 0,
    status: "",
    experience_level: "",
    position: "",
  });

  const notification = Helpers.notification;

  useEffect(() => {
    if (!isOpenConfirmForm) {
      const newSteps = CreateCampaignSteps.filter(
        (item, index) => index != CreateCampaignSteps.length - 1
      );
      setSteps?.(newSteps);
      setCurrentStep(0);
    } else {
      console.log(1);
      setSteps?.(CreateCampaignSteps);
    }
  }, [isOpenConfirmForm]);

  // FETCH CAMPAIGN
  const fetchCampaign = async () => {
    try {
      const response = await businessLogicApi.GetCampaigns();
      if (response.status == 200) {
        setCampaignList(response.data.campaigns);
      }
    } catch (error) {
      notification.error("Fail to fetch campaign. Please try agian");
    }
  };

  // FETCH POSITION
  const fetchPosition = async () => {
    try {
      const response = await businessLogicApi.GetPosition();
      if (response.status == 200) {
        setPositionList(response.data);
      }
    } catch (error) {
      notification.error("Fail to fetch position. Please try again");
    }
  };

  // FETCH LEVEL
  const fetchLevel = async () => {
    try {
      const response = await businessLogicApi.GetLevel();
      if (response.status == 200) {
        setLevelList(response.data);
      }
    } catch (error) {
      notification.error("Fail to fetch position. Please try again");
    }
  };

  useEffect(() => {
    if (!isFetch) {
      fetchCampaign();
      fetchPosition();
      fetchLevel();
      setTimeout(() => {
        setIsFetch(true);
      }, 700);
    }
  }, []);

  // SET DEFAULT VALUE FOR THE WHOLE PHASE
  useEffect(() => {
    if (tasks.length > 0) {
      const newStartDate = new Date(tasks[0].start_date);
      const newEndDate = new Date(tasks[tasks.length - 1].end_date);
      setStartDateAllPhase(newStartDate);
      setEndDateAllPhase(newEndDate);
      setCampaignForm({
        ...campaignForm,
        start_date: Helpers.formattedUTC(tasks[0]?.start_date, true),
        end_date: Helpers.formattedUTC(tasks[tasks.length - 1]?.end_date, true),
        duration: Helpers.calculateDuration(
          tasks[0]?.start_date,
          tasks[tasks.length - 1]?.end_date
        ),
      });
    }
  }, [tasks]);

  // CREATE CAMPAIGN
  const createCampaign = async () => {
    if (!validateForm()) return;
    try {
      const response = await businessLogicApi.CreateCampaign({
        campaign_name: campaignForm.campaign_name,
        budget: campaignForm.budget,
        start_date: campaignForm.start_date,
        end_date: campaignForm.end_date,
        duration: campaignForm.duration,
        position: campaignForm.position,
        experience_level: campaignForm.experience_level,
        phases: formatPhases(),
      });
      if (response.status === 200) {
        if (isOpen) {
          localStorage.setItem("tour", "true");
        }
        Helpers.redirect(
          `/recruiter/panel/sourcing?id=${response.data.campaign_id}`
        );
      }
    } catch (error) {
      notification.error("Can not create campaign. Please try again");
      return false;
    }
  };

  // FORMAT PHASES
  const formatPhases = (): string => {
    const tasksPayload: any[] = [];

    tasks.forEach((item) => {
      const {
        color,
        open,
        progress,
        $no_start,
        $no_end,
        $rendered_type,
        $calculate_duration,
        $effective_calendar,
        $source,
        $target,
        parent,
        $open,
        $rendered_parent,
        $level,
        $local_index,
        $expanded_branch,
        $resourceAssignments,
        $index,
        ...propToStore
      } = item;

      tasksPayload.push(propToStore);
    });
    return JSON.stringify(tasksPayload);
  };

  // VALIDATE FORM CREATE CAMPAIGN BEFORE SUBMIT
  const validateForm = () => {
    if (
      campaignForm.campaign_name == "" ||
      campaignForm.start_date == "" ||
      campaignForm.end_date == "" ||
      campaignForm.position == "" ||
      campaignForm.experience_level == ""
    ) {
      notification.error("Please fill into require fields");
      return false;
    } else if (campaignForm.budget == 0) {
      notification.warning("Minimum budget must be 100 HeppCoins");
      return false;
    }
    return true;
  };

  // CHANGE CAMPAIGN FORM
  const handleChangeCampaignForm = (value: any, type: string) => {
    if (type == "start_date") {
      setStartDateAllPhase(Helpers.unformattedUTC(value));
      setCampaignForm({
        ...campaignForm,
        [type]: value,
        duration: Helpers.calculateDuration(
          Helpers.unformattedUTC(value),
          Helpers.unformattedUTC(campaignForm.end_date)
        ),
      });
      setIsDateAllPhaseChange(true);
    } else if (type == "end_date") {
      setEndDateAllPhase(Helpers.unformattedUTC(value));
      setCampaignForm({
        ...campaignForm,
        [type]: value,
        duration: Helpers.calculateDuration(
          Helpers.unformattedUTC(campaignForm.start_date),
          Helpers.unformattedUTC(value)
        ),
      });
      setIsDateAllPhaseChange(true);
    } else {
      setCampaignForm({ ...campaignForm, [type]: value });
    }
  };

  // DEFINE POSTION LIST
  const positionListOptions = (): DefaultOptionType[] => {
    const option: DefaultOptionType[] = [];
    if (positionList.length > 0) {
      positionList.forEach((item: any) => {
        const ite = {
          value: item.position_id,
          label: item.position_name,
        };
        option.push(ite);
      });
    }

    return option;
  };

  // DEFINE LEVEL LIST
  const levelListOptions = (): DefaultOptionType[] => {
    const option: DefaultOptionType[] = [];
    if (levelList.length > 0) {
      levelList.forEach((item: any, index: number) => {
        const ite = {
          value: item.experience_id,
          label: item.name,
        };
        option.push(ite);
      });
    }

    return option;
  };

  return (
    <>
      {!isFetch ? (
        <div className="Campaign-overlay">
          <Spin className="spin" size="large" />
        </div>
      ) : (
        <div className="Campaign">
          {campaignList?.length > 0 && !isCreateNew ? (
            <CampaignList
              campaignList={campaignList}
              setCampaignList={setCampaignList}
              setIsCreateNew={setIsCreateNew}
            />
          ) : (
            <Card loading={!isFetch} className="card-begin">
              {!isOpenCreateForm && !isCreateNew ? (
                <div className="begin-section">
                  <div className="title">Create New Campaign</div>
                  <div className="subtitle">
                    Create an engaging campaign that attracts top
                    <br /> talents and showcases your vibrant company culture!
                  </div>
                  <Button
                    className="create-btn"
                    onClick={() => setIsBeginModal(true)}
                    icon={<img src={Coin} />}
                  >
                    +30
                  </Button>
                </div>
              ) : (
                <div className="create-campaign-section">
                  <Row className="h-100">
                    <Col span={7}>
                      <CampaignForm
                        campaignList={campaignList}
                        campaignForm={campaignForm}
                        handleChangeCampaignForm={handleChangeCampaignForm}
                        setIsOpenCreateForm={setIsOpenCreateForm}
                        setIsCreateNew={setIsCreateNew}
                        positionListOptions={positionListOptions}
                        levelListOptions={levelListOptions}
                        startDateAllPhase={startDateAllPhase}
                        endDateAllPhase={endDateAllPhase}
                      />
                    </Col>
                    <Col span={17}>
                      <div className="gantt-group">
                        <div className="d-flex ms-auto mb-3">
                          <Segmented
                            options={["Daily", "Weekly", "Monthly"]}
                            defaultValue={ganttView}
                            onChange={(value) => setGanttView(value)}
                          />
                        </div>
                        <div className="gantt-form">
                          <GanttChart
                            tasks={tasks}
                            setTasks={setTasks}
                            ganttView={ganttView}
                            startDateAllPhase={startDateAllPhase}
                            endDateAllPhase={endDateAllPhase}
                            isDateAllPhaseChange={isDateAllPhaseChange}
                            setIsDateAllPhaseChange={setIsDateAllPhaseChange}
                          />
                        </div>
                        <div className="campaign-submit">
                          <Button
                            className="create-campaign-btn"
                            onClick={() =>
                              setIsOpenConfirmForm(!isOpenConfirmForm)
                            }
                          >
                            Create
                          </Button>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
              )}
            </Card>
          )}

          <BeginModal
            setIsTourOpen={setIsOpen}
            setIsOpenCreateForm={setIsOpenCreateForm}
            isOpen={isBeginModal}
            closeModal={() => setIsBeginModal(false)}
          />

          <ConfirmModal
            closeModal={() => setIsOpenConfirmForm(false)}
            isOpen={isOpenConfirmForm}
            handleCreateCampaign={createCampaign}
          />
        </div>
      )}
    </>
  );
};

export default Campaign;
